import { Logger } from 'loglevel';
import buildQuery from 'odata-query';
import { FilterCondition } from '../../types/filter.types';

const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const extractFilterValue = (query: string): string => {
  // Use manual parsing instead of URLSearchParams to avoid interpreting '+' as space.
  // This regex captures the raw, percent-encoded value of the $filter parameter.
  const match = /[?&]\$filter=([^&]*)/.exec(query);

  if (!match) {
    return '';
  }

  try {
    // decodeURIComponent correctly handles '%20' for spaces and does not treat '+' specially.
    return decodeURIComponent(match[1]);
  } catch {
    // In case of malformed encoding, fail safely by returning an empty filter.
    return '';
  }
};

type ODataFilterInput = string | Record<string, unknown> | Array<string | Record<string, unknown>>;
type GuidLiteralFormat = 'v4' | 'legacy';

export class ODataDsnFilterBuilder {
  constructor(
    private readonly conditions: FilterCondition[],
    private readonly logger: Logger,
    private readonly guidLiteralFormat: GuidLiteralFormat = 'v4',
  ) {}

  buildOdataFilter(): string {
    const activeConditions = this.conditions.filter(condition => condition.isEnabled);
    this.logger.debug('Building filter with conditions:', activeConditions);

    if (activeConditions.length === 0) {
      return '';
    }

    const filterString = activeConditions
      .map(condition => this.getFilterString(condition))
      .filter(Boolean)
      .join(' and ');

    // return `(${filterString})`;
    return filterString;
  }

  private buildFilterString(filter: ODataFilterInput): string {
    const queryString = buildQuery({ filter });

    return extractFilterValue(queryString);
  }

  private getFilterString(condition: FilterCondition): string {
    switch (condition.type) {
      case 'date':
        return this.getDateFilter(condition.value);
      case 'user':
        return this.getUserFilter(condition.value);
      case 'test':
        return this.getTestFilter(condition.value);
      case 'status':
        return this.getStatusFilter(condition.value);
      case 'nature':
        return this.getNatureFilter(condition.value);
      case 'search':
        return this.buildSearchFilter(condition.value);
      default:
        return '';
    }
  }

  private getDateFilter(value: { startDate: Date | null; endDate: Date | null }): string {
    const { startDate, endDate } = value;

    const dateComparisons: { ge?: Date; lt?: Date } = {};

    if (startDate instanceof Date && !Number.isNaN(startDate.getTime())) {
      dateComparisons.ge = startDate;
    }

    if (endDate instanceof Date && !Number.isNaN(endDate.getTime())) {
      dateComparisons.lt = endDate;
    }

    if (Object.keys(dateComparisons).length === 0) {
      return '';
    }

    return this.buildFilterString({ DateCreationInterchange: dateComparisons });
  }

  private getUserFilter(value: string | string[]): string {
    const values = Array.isArray(value) ? value : [value];
    const guidConditions = values
      .map(v => ODataDsnFilterBuilder.toGuidLiteral(v, this.guidLiteralFormat))
      .filter((g): g is string => g !== null)
      .map(g => `DsnEnvoiSageDirectObj/InstanceIdUser eq ${g}`);

    if (guidConditions.length === 0) {
      return '';
    }

    if (guidConditions.length === 1) {
      return guidConditions[0];
    }

    return `(${guidConditions.join(' or ')})`;
  }

  private getTestFilter(value: boolean): string {
    return this.buildFilterString({ Test: value });
  }

  private getStatusFilter(value: string | string[]): string {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '';
      }

      if (value.length === 1) {
        return this.buildFilterString({ Status: value[0] });
      }

      return this.buildFilterString({ or: value.map(status => ({ Status: status })) });
    }

    return this.buildFilterString({ Status: value });
  }

  private getNatureFilter(value: string | string[]): string {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '';
      }

      if (value.length === 1) {
        return this.buildFilterString({ Nature: value[0] });
      }

      return this.buildFilterString({ or: value.map(nature => ({ Nature: nature })) });
    }

    return this.buildFilterString({ Nature: value });
  }

  private buildSearchFilter(searchTerm: string): string {
    const normalizedTerm = searchTerm.trim();
    if (!normalizedTerm) return '';

    const cleanTerm = ODataDsnFilterBuilder.escapeODataString(normalizedTerm);
    const conditions = [
      `contains(DsnTiersDeclarantObj/Siret, '${cleanTerm}')`,
      `DsnDeclarationTiersObj/any(x1: contains(tolower(x1/DsnTiersObj/RaisonSociale), tolower('${cleanTerm}')))`,
      `(Nature ne 'DsnMensuelle' and DsnEmployeObj/any(x1: contains(tolower(x1/NomComplet), tolower('${cleanTerm}'))))`,
      `DsnDeclarationTiersObj/any(x1: (x1/DsnTiersObj/TypeTiers eq 'Redevable') and contains(x1/DsnTiersObj/Siret, '${cleanTerm}'))`,
    ];

    const rawFilter = `(${conditions.join(' or ')})`;
    return rawFilter;
  }

  private static escapeODataString(value: string): string {
    return value.replace(/'/g, "''");
  }

  private static toGuidLiteral(value: string, format: GuidLiteralFormat): string | null {
    const guid = value.trim();
    if (!GUID_REGEX.test(guid)) {
      return null;
    }

    if (format === 'legacy') {
      return `guid'${guid}'`;
    }

    return guid;
  }
}

export default ODataDsnFilterBuilder;
